import { ReactNode } from 'react'
import cx from 'classnames'
import { SUPPORTED_FONTS } from 'prose-core'

import Styles from './StyleSection.module.scss'
import {
  AvailableWeights,
  WeightNamesMap,
} from 'common/Prose/Toolbar/components/Format/components/MainStylePane/components/FontWeight/util'
import { FontFamily } from 'fonts'
import { FontFamilyType } from 'utils/fontUtils'
import { Props as DropdownSelectProps } from 'common/DropdownSelect/DropdownSelect'
import { ReactComponent as AsTypedIcon } from './asTyped.svg'
import { ReactComponent as CenterAlignIcon } from 'assets/icons/alignCenter24.svg'
import { ReactComponent as JustifyAlignIcon } from 'assets/icons/alignJustify24.svg'
import { ReactComponent as LeftAlignIcon } from 'assets/icons/alignLeft24.svg'
import { ReactComponent as LowercaseIcon } from './lowercase.svg'
import { ReactComponent as RightAlignIcon } from 'assets/icons/alignRight24.svg'
import { ReactComponent as TitlecaseIcon } from './titlecase.svg'
import { ReactComponent as UppercaseIcon } from './uppercase.svg'
import { getFontSvg } from 'utils/fontUtils'

export const ColorOptions = [
  Styles.navy70,
  Styles.blue70,
  Styles.green70,
  Styles.gold70,
  Styles.orange70,
  Styles.red70,
  Styles.pink70,
  Styles.violet70,
  Styles.navy50,
  Styles.blue50,
  Styles.green50,
  Styles.gold50,
  Styles.orange50,
  Styles.red50,
  Styles.pink50,
  Styles.violet50,
  Styles.navy30,
  Styles.blue30,
  Styles.green30,
  Styles.gold30,
  Styles.orange30,
  Styles.red30,
  Styles.pink30,
  Styles.violet30,
  Styles.navy10,
  Styles.blue10,
  Styles.green10,
  Styles.gold10,
  Styles.orange10,
  Styles.red10,
  Styles.pink10,
  Styles.violet10,
  Styles.gray90,
  Styles.gray70,
  Styles.gray40,
  Styles.gray10,
]

export const FontOptions: { label: ReactNode; shortLabel: string; value: FontFamily }[] = SUPPORTED_FONTS.map(
  (font) => ({
    label: getFontSvg(font),
    shortLabel: font,
    value: font,
  }),
)

export const FontSizeOptions = [8, 12, 16, 24, 32, 48, 64, 72, 96, 112].map((number) => ({
  label: number,
  value: number,
}))

export const FontStyleOptions = [
  { label: 'Normal', value: 'normal' },
  { label: <em>Italic</em>, value: 'italic' },
]
export const FontWeightOptions = (fontFamily?: FontFamilyType): DropdownSelectProps['options'] => {
  if (!fontFamily) return []
  if (fontFamily in AvailableWeights)
    return AvailableWeights[fontFamily].map((weight) => ({
      label: <span style={{ fontWeight: parseInt(weight) }}>{WeightNamesMap[weight]}</span>,
      value: weight.toString(),
    }))

  return []
}

export const LineHeightOptions = [1, 1.15, 1.5, 2].map((number) => ({ label: number, value: number }))

export const PaddingOptions = [0, 2, 4, 8, 12, 16, 24, 32, 48, 64, 72, 96, 112].map((number) => ({
  label: number,
  value: number,
}))

export const TextAlignOptions = [
  {
    label: (
      <>
        <LeftAlignIcon className={Styles.labelIcon} />
        Left
      </>
    ),
    shortLabel: <LeftAlignIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'left',
  },
  {
    label: (
      <>
        <CenterAlignIcon className={Styles.labelIcon} />
        Center
      </>
    ),
    shortLabel: <CenterAlignIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'center',
  },
  {
    label: (
      <>
        <RightAlignIcon className={Styles.labelIcon} />
        Right
      </>
    ),
    shortLabel: <RightAlignIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'right',
  },
  {
    label: (
      <>
        <JustifyAlignIcon className={Styles.labelIcon} />
        Justify
      </>
    ),
    shortLabel: <JustifyAlignIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'justify',
  },
]

export const TextDecorationOptions = [
  { label: 'None', value: 'none' },
  { label: <s>Strikethrough</s>, value: 'line-through' },
  { label: <u>Underline</u>, value: 'underline' },
]

export const TextTransformOptions = [
  {
    label: (
      <>
        <AsTypedIcon className={Styles.labelIcon} />
        As typed
      </>
    ),
    shortLabel: <AsTypedIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'none',
  },
  {
    label: (
      <>
        <UppercaseIcon className={Styles.labelIcon} />
        Uppercase
      </>
    ),
    shortLabel: <UppercaseIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'uppercase',
  },
  {
    label: (
      <>
        <LowercaseIcon className={Styles.labelIcon} />
        Lowercase
      </>
    ),
    shortLabel: <LowercaseIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'lowercase',
  },
  {
    label: (
      <>
        <TitlecaseIcon className={Styles.labelIcon} />
        Titlecase
      </>
    ),
    shortLabel: <TitlecaseIcon className={cx(Styles.labelIcon, Styles.shortLabelIcon)} />,
    value: 'capitalize',
  },
]
